import { 
  doc,
  updateDoc,
  serverTimestamp,
  Firestore,
  GeoPoint,
  DocumentReference,
  getDoc,
  FieldValue
} from "firebase/firestore";
import { CampLocation } from "@/types/campLocation";
import { createLocationVersion, updateLocationVersion } from "./locationVersionCreateService";
import { forceFirestoreTimestamps } from "@/utils/firestoreUtils";
import { omitServerOwnedCampLocationFields } from "@/utils/serverOwnedCampLocationFields";
import { updateOrgCountForOrgChange, updateOrgCountForWorkflowChange } from "./orgPublishedLocationsCounter";
import { shouldForceHideFromWorkflowStatus, didForceHideChangeForWorkflowTransition } from "@/utils/workflowForceHide";
import { updateAllOffersForLocation } from "@/services/offer/offerBatchUpdateService";
import { isLocationEditLiveSource } from "@/config/locationEditFlag";

/**
 * Helper function to check if a value is a Firestore special object that should be preserved
 * (like serverTimestamp, DocumentReference, GeoPoint, etc.)
 */
const isFirestoreSpecialObject = (value: any): boolean => {
  return (
    value instanceof GeoPoint ||
    value instanceof DocumentReference ||
    value instanceof FieldValue ||
    (value && typeof value === 'object' && 
     (value._methodName === 'serverTimestamp' || 
      (value._delegate && value._delegate._methodName === 'serverTimestamp')))
  );
};

// Helper function to clean any undefined values from the data
const sanitizeForFirestore = (data: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(data)) {
    // Skip undefined values
    if (value === undefined) continue;
    
    // Handle null explicitly
    if (value === null) {
      result[key] = null;
      continue;
    }
    
    // CRITICAL: Preserve Firestore special objects (serverTimestamp, DocumentReference, GeoPoint, etc.)
    if (isFirestoreSpecialObject(value)) {
      result[key] = value;
      continue;
    }
    
    // Clean nested objects, but avoid recursing into Firestore special objects
    if (typeof value === 'object' && !Array.isArray(value)) {
      result[key] = sanitizeForFirestore(value);
      continue;
    }
    
    // Handle arrays - CRITICAL: Special handling for photos array
    if (Array.isArray(value)) {
      result[key] = value.map(item => {
        // Skip undefined items
        if (item === undefined) return null;
        
        // Preserve Firestore special objects in arrays
        if (isFirestoreSpecialObject(item)) {
          return item;
        }
        
        // If this is a photo object with createdAt, preserve serverTimestamp placeholders
        if (typeof item === 'object' && item !== null) {
          const cleanItem = { ...item };
          
          // Remove colorScheme completely from variations
          if ('key' in cleanItem && 'colorScheme' in cleanItem) {
            delete cleanItem.colorScheme;
          }
          
          // CRITICAL: For photo objects, preserve createdAt if it's a serverTimestamp
          if ('createdAt' in cleanItem && isFirestoreSpecialObject(cleanItem.createdAt)) {
            console.log('[campLocationUpdateService] Preserving serverTimestamp in photo createdAt during sanitization');
            // Keep the serverTimestamp placeholder as-is
            return {
              ...sanitizeForFirestore(cleanItem),
              createdAt: cleanItem.createdAt // Override with the original serverTimestamp
            };
          }
          
          return typeof cleanItem === 'object' ? sanitizeForFirestore(cleanItem) : cleanItem === undefined ? null : cleanItem;
        }
        return item === undefined ? null : item;
      });
      continue;
    }
    
    // Store regular values
    result[key] = value;
  }
  
  return result;
};

// CRITICAL SECURITY FUNCTION: Prepares data for Firestore update while ensuring critical fields are correctly formatted
const prepareLocationUpdateData = (
  cleanedData: Record<string, any>, 
  userRef: DocumentReference | null
): Record<string, any> => {
  // Make a copy of the cleaned data
  const updateData = { ...cleanedData };
  
  // CRITICAL: Remove any incorrectly formatted versions of these fields that might have come from form data
  // These fields MUST be set in the correct Firestore format or the application breaks
  delete updateData.lastEditedBy;   // Will be set correctly below as DocumentReference
  delete updateData.lastEditedAt;   // Will be set correctly below as Timestamp
  delete updateData.createdAt;      // CRITICAL: Never allow createdAt to be overwritten - it should remain as original Firestore Timestamp
  omitServerOwnedCampLocationFields(updateData); // KB-3418: rollup-owned; a stale copy would be refused by the rules

  // CRITICAL: Ensure _geoloc is ALWAYS stored as GeoPoint if it exists
  if (updateData._geoloc) {
    // If _geoloc exists but is not a GeoPoint, convert it or remove it
    if (!(updateData._geoloc instanceof GeoPoint)) {
      // Try to convert from object format { lat, lng } or { latitude, longitude }
      const geoloc = updateData._geoloc;
      if (geoloc && typeof geoloc === 'object') {
        const lat = geoloc.lat || geoloc.latitude;
        const lng = geoloc.lng || geoloc.longitude;
        if (typeof lat === 'number' && typeof lng === 'number') {
          updateData._geoloc = new GeoPoint(lat, lng);
        } else {
          // Invalid format, remove it to prevent corruption
          delete updateData._geoloc;
        }
      } else {
        // Not an object, remove it
        delete updateData._geoloc;
      }
    }
  }
  
  return updateData;
};

export const updateCampLocation = async (
  db: Firestore, 
  id: string, 
  data: Partial<CampLocation>, 
  userId: string, 
  changeDescription?: string, 
  forceUpdateLatestDraft: boolean = false,
  isAfterPublish: boolean = false // New parameter to indicate if this is after publishing
) => {
  try {
    console.log(`[campLocationUpdateService] Updating camp location ${id} with data:`, 
      JSON.stringify(data).substring(0, 200) + "...");
    console.log(`[campLocationUpdateService] Change description: "${changeDescription}", userId: ${userId}, forceUpdateLatestDraft: ${forceUpdateLatestDraft}`);

    // Clean data to replace undefined with null
    const cleanedData = sanitizeForFirestore(data);

    // Public URL slug lives only on campLocations; do not merge into campLocationVersions snapshots.
    let slugRootOnly: Record<string, unknown> | null = null;
    if (Object.prototype.hasOwnProperty.call(cleanedData, "slug")) {
      const raw = (cleanedData as Record<string, unknown>).slug;
      const asString = raw === null || raw === undefined ? "" : String(raw);
      slugRootOnly = { slug: asString };
      delete (cleanedData as Record<string, unknown>).slug;
    }

    // If workflowStatus is being updated, also set forceHide on campLocations and (via version write) on draft snapshot
    if (Object.prototype.hasOwnProperty.call(cleanedData, 'workflowStatus')) {
      (cleanedData as any).forceHide = shouldForceHideFromWorkflowStatus((cleanedData as any).workflowStatus);
    }
    console.log(`[campLocationUpdateService] Cleaned data:`, 
      JSON.stringify(cleanedData).substring(0, 200) + "...");

    const locRef = doc(db, "campLocations", id);
    const userRef = userId !== "system" ? doc(db, "users", userId) : null;
    
    // Get the current location document to access latestVersionId and full location data
    const locSnapshot = await getDoc(locRef);
    if (!locSnapshot.exists()) {
      throw new Error(`Location ${id} not found`);
    }
    
    const locationData = locSnapshot.data();
    // KB-4564 ruling B — the programs as they were before this save, so the offer propagation below lets a
    // window that merely copied an old program follow a re-time (and carries every other held window).
    const preSaveVariations = Array.isArray((locationData as any).variations) ? (locationData as any).variations : null;
    const oldWorkflowStatus = (locationData as any).workflowStatus || null;
    const oldProvidedByOrg = (locationData as any).providedByOrg as any;
    const oldOrgId = typeof oldProvidedByOrg === 'string' ? oldProvidedByOrg : (oldProvidedByOrg && (oldProvidedByOrg as any).id) ? (oldProvidedByOrg as any).id : null;
    const latestVersionId = locationData.latestVersionId ? 
      (typeof locationData.latestVersionId === 'string' ? 
        locationData.latestVersionId : 
        locationData.latestVersionId.id) : 
      null;
      
    console.log(`[campLocationUpdateService] Current latest version ID: ${latestVersionId}`);

    // Handle providedByOrg reference
    if (cleanedData.providedByOrg) {
      const orgId = typeof cleanedData.providedByOrg === 'string'
        ? cleanedData.providedByOrg
        : typeof cleanedData.providedByOrg === 'object' && 'id' in cleanedData.providedByOrg
          ? cleanedData.providedByOrg.id
          : null;
      
      if (orgId) {
        console.log(`[campLocationUpdateService] Converting providedByOrg to document reference: ${orgId}`);
        const orgRef = doc(db, "campOrganizations", orgId);
        cleanedData.providedByOrg = orgRef as DocumentReference;
      }
    }

    // Process latLng
    if (cleanedData.address?.latLng && typeof cleanedData.address.latLng === 'string') {
      const [lat, lng] = cleanedData.address.latLng.split(',').map(coord => parseFloat(coord.trim()));
      if (!isNaN(lat) && !isNaN(lng)) {
        console.log(`[campLocationUpdateService] Converting latLng string to GeoPoint: ${lat}, ${lng}`);
        const geoPoint = new GeoPoint(lat, lng);
        if (cleanedData.address) {
          cleanedData.address = {
            ...cleanedData.address,
            latLng: geoPoint
          };
        }
        
        // CRITICAL: Store _geoloc as GeoPoint instead of a Map with lat/lng properties
        cleanedData._geoloc = geoPoint; // Use GeoPoint instead of object
      }
    }

    // Clean up campExperiences
    if (cleanedData.campExperiences) {
      cleanedData.campExperiences = cleanedData.campExperiences.map(exp => {
        const cleanExp = { ...exp };
        if (!Array.isArray(cleanExp.themeOtherKeys)) {
          cleanExp.themeOtherKeys = [];
        }
        return cleanExp;
      });
    }

    // KB-3308: live-only path. Skip BOTH version branches entirely — no createLocationVersion /
    // updateLocationVersion, no latestVersionId write. Keep the live updateDoc and the
    // lastEditedBy/lastEditedAt stamp exactly as the flag-off branches do (same single write,
    // KB-3471). changeDescription / forceUpdateLatestDraft / isAfterPublish become no-ops here.
    if (isLocationEditLiveSource()) {
      console.log(`[campLocationUpdateService] Live-only update — writing the live doc only, no version write`);

      const safeUpdateData = prepareLocationUpdateData(cleanedData, userRef);
      if (slugRootOnly) Object.assign(safeUpdateData, slugRootOnly);

      const timestampSafeData = forceFirestoreTimestamps(safeUpdateData);
      // Editor stamp rides in the SAME write as the data (see the flag-off branches for the full
      // KB-3471 rationale). No latestVersionId — there is no version to point at.
      timestampSafeData.lastEditedBy = userRef;
      timestampSafeData.lastEditedAt = serverTimestamp();

      await updateDoc(locRef, timestampSafeData);

      // Post-write: adjust org counters for ANY workflow/org change (identical to the flag-off
      // branches — a workflow/org change still moves the same counters on the live doc).
      if (Object.prototype.hasOwnProperty.call(cleanedData, 'workflowStatus')) {
        const newWorkflowStatus = (cleanedData as any).workflowStatus || null;
        if (newWorkflowStatus !== oldWorkflowStatus) {
          await updateOrgCountForWorkflowChange(db, id, oldWorkflowStatus, newWorkflowStatus);
        }
        if (didForceHideChangeForWorkflowTransition(oldWorkflowStatus, newWorkflowStatus)) {
          await updateAllOffersForLocation(db, id, userId, preSaveVariations);
        }
      }
      if (Object.prototype.hasOwnProperty.call(cleanedData, 'providedByOrg')) {
        const provided = (cleanedData as any).providedByOrg as any;
        const newOrgId = provided == null ? null : (typeof provided === 'string' ? provided : ((provided as any).id || null));
        if (newOrgId !== oldOrgId) {
          await updateOrgCountForOrgChange(db, id, oldOrgId, newOrgId);
        }
      }

      return true;
    }

    // CRITICAL FIX: Named Version Case only happens when explicitly providing a name AND forceUpdateLatestDraft is false
    // We apply the name to the current version, then create a new version for future edits
    if (changeDescription && changeDescription.trim().length > 0 && !forceUpdateLatestDraft) {
      console.log(`[campLocationUpdateService] Named version save with description: "${changeDescription}"`);
      
      if (!latestVersionId) {
        console.log(`[campLocationUpdateService] No latest version found, creating new named version`);
        // If somehow there's no latest version, create one with the name
        const namedVersionId = await createLocationVersion(
          db,
          { ...cleanedData, id } as CampLocation,
          userId,
          'update',
          changeDescription,
          'draft',
          undefined,
          true // Mark as named version
        );
        
        console.log(`[campLocationUpdateService] Created new named version ${namedVersionId}`);
      } else {
        // First, update the current version with the name (mark as named version)
        console.log(`[campLocationUpdateService] Updating existing version ${latestVersionId} to mark as named version`);
        await updateLocationVersion(
          db,
          latestVersionId,
          { ...cleanedData, id } as CampLocation,
          userId,
          changeDescription,
          undefined,
          true // Mark as named version
        );
        
        console.log(`[campLocationUpdateService] Successfully marked version ${latestVersionId} as named`);
      }

      // Immediately create a new version to become the latest
      // This preserves the named version and gives us a fresh working copy
      const newVersionChangeDescription = isAfterPublish 
        ? 'New draft created after publishing' 
        : 'New draft created after naming previous version';
      
      console.log(`[campLocationUpdateService] Creating new version with description: ${newVersionChangeDescription}`);
      
      const newVersionId = await createLocationVersion(
        db,
        { ...cleanedData, id } as CampLocation,
        userId,
        'create',
        newVersionChangeDescription,
        'draft',
        undefined,
        false
      );

      console.log(`[campLocationUpdateService] Created new version ${newVersionId} as latest`);

      // CRITICAL FIX: Prepare data for update ensuring critical fields are in correct format
      const safeUpdateData = prepareLocationUpdateData(cleanedData, userRef);
      if (slugRootOnly) Object.assign(safeUpdateData, slugRootOnly);
      
      // 🔥 CRITICAL: Force all timestamp fields to proper Firestore Timestamps as final step
      const timestampSafeData = forceFirestoreTimestamps(safeUpdateData);
      console.log(`[campLocationUpdateService] Applied forceFirestoreTimestamps before Firestore write`);

      // KB-3471: the editor stamp must ride in the SAME write as the data. The attribution trigger
      // resolves the actor from afterData.lastEditedBy of the write that changed the model fields, so
      // a separate stamp write credits the PREVIOUS editor. lastEditedBy is a raw DocumentReference and
      // lastEditedAt a serverTimestamp() sentinel — attached after forceFirestoreTimestamps, which
      // coerces timestamp *data* fields and leaves a ref/sentinel untouched (proven by
      // forceFirestoreTimestamps.kb3471.test.ts).
      timestampSafeData.latestVersionId = newVersionId;
      timestampSafeData.lastEditedBy = userRef;
      timestampSafeData.lastEditedAt = serverTimestamp();

      // Single write: model data and editor stamp together.
      await updateDoc(locRef, timestampSafeData);

      // Post-write: adjust org counters for ANY workflow/org change
      if (Object.prototype.hasOwnProperty.call(cleanedData, 'workflowStatus')) {
        const newWorkflowStatus = (cleanedData as any).workflowStatus || null;
        if (newWorkflowStatus !== oldWorkflowStatus) {
          await updateOrgCountForWorkflowChange(db, id, oldWorkflowStatus, newWorkflowStatus);
        }
        if (didForceHideChangeForWorkflowTransition(oldWorkflowStatus, newWorkflowStatus)) {
          await updateAllOffersForLocation(db, id, userId, preSaveVariations);
        }
      }
      if (Object.prototype.hasOwnProperty.call(cleanedData, 'providedByOrg')) {
        const provided = (cleanedData as any).providedByOrg as any;
        const newOrgId = provided == null ? null : (typeof provided === 'string' ? provided : ((provided as any).id || null));
        if (newOrgId !== oldOrgId) {
          await updateOrgCountForOrgChange(db, id, oldOrgId, newOrgId);
        }
      }

      return true;
    } else {
      // Regular update flow or force update to latest draft - update the latest draft without creating new version
      console.log(`[campLocationUpdateService] Regular update/auto-save - updating latest draft (forceUpdate=${forceUpdateLatestDraft})`);
      
      let versionId = latestVersionId;
      
      if (versionId && (forceUpdateLatestDraft || changeDescription === undefined)) {
        // Update existing version if we're forcing updates to latest draft or if this is an auto-save
        console.log(`[campLocationUpdateService] Updating existing version: ${versionId}`);
        
        // IMPORTANT: For partial updates with forceUpdateLatestDraft, ensure we get the current version data first
        // so we don't lose any fields during the update
        if ((forceUpdateLatestDraft || Object.keys(cleanedData).length < 10)) {  // Always merge for manual saves and partial updates
          console.log(`[campLocationUpdateService] Detected partial update or manual save, applying full merge`);
          
          // Get current version
          const versionRef = doc(db, "campLocationVersions", versionId);
          const versionDoc = await getDoc(versionRef);
          
          if (versionDoc.exists()) {
            const currentVersionData = versionDoc.data();
            if (currentVersionData && currentVersionData.data) {
              console.log(`[campLocationUpdateService] Merging with existing version data`);
              
              // Merge with existing data for manual save or partial update
              // Only update the specific fields provided in data parameter
              const mergedData = {
                ...currentVersionData.data,
                ...cleanedData,
                id  // Always ensure ID is preserved
              };
              
              // Clean merged data again just to be sure
              const finalData = sanitizeForFirestore(mergedData);
              
              console.log(`[campLocationUpdateService] Merged data contains ${Object.keys(finalData).length} fields`);
              
              // Update with the merged data
              await updateLocationVersion(
                db,
                versionId,
                finalData as CampLocation,
                userId,
                changeDescription || 'Update from manual save button',
                undefined,
                false
              );
              
              console.log(`[campLocationUpdateService] Successfully updated version ${versionId} with merged data`);
            }
          } else {
            // Version doesn't exist - unusual case
            console.warn(`[campLocationUpdateService] Version ${versionId} not found for merge update`);
          }
        } else {
          // Regular update without special merging
          await updateLocationVersion(
            db,
            versionId,
            { ...cleanedData, id } as CampLocation,
            userId,
            changeDescription || 'Update from manual save button',
            undefined,
            false
          );
          console.log(`[campLocationUpdateService] Successfully updated version ${versionId}`);
        }
      } else {
        // Create a new version if we can't find a latest version ID to update
        // This should rarely happen, mainly when initializing a location
        console.log(`[campLocationUpdateService] No existing version to update, creating new`);
        versionId = await createLocationVersion(
          db,
          { ...cleanedData, id } as CampLocation,
          userId,
          'update',
          changeDescription || 'Initial version',
          'draft',
          undefined,
          false
        );
        
        console.log(`[campLocationUpdateService] Created new version ${versionId} as there was no latest version`);
      }

      // CRITICAL FIX: Prepare data for update ensuring critical fields are in correct format
      const safeUpdateData = prepareLocationUpdateData(cleanedData, userRef);
      if (slugRootOnly) Object.assign(safeUpdateData, slugRootOnly);
      
      // 🔥 CRITICAL: Force all timestamp fields to proper Firestore Timestamps as final step
      const timestampSafeData = forceFirestoreTimestamps(safeUpdateData);
      console.log(`[campLocationUpdateService] Applied forceFirestoreTimestamps before Firestore write`);

      // KB-3471: the editor stamp must ride in the SAME write as the data (see the named-version
      // branch above for the full rationale — the attribution trigger reads afterData.lastEditedBy
      // of the write that changed the model fields). Attached after forceFirestoreTimestamps so the
      // DocumentReference and serverTimestamp() sentinel are left untouched.
      timestampSafeData.latestVersionId = versionId;
      timestampSafeData.lastEditedBy = userRef;
      timestampSafeData.lastEditedAt = serverTimestamp();

      // Single write: model data and editor stamp together.
      await updateDoc(locRef, timestampSafeData);

      console.log(`[campLocationUpdateService] Updated location document to point to version ${versionId} and applied data changes`);

      // Post-write: adjust org counters for ANY workflow/org change
      if (Object.prototype.hasOwnProperty.call(cleanedData, 'workflowStatus')) {
        const newWorkflowStatus = (cleanedData as any).workflowStatus || null;
        if (newWorkflowStatus !== oldWorkflowStatus) {
          await updateOrgCountForWorkflowChange(db, id, oldWorkflowStatus, newWorkflowStatus);
        }
        if (didForceHideChangeForWorkflowTransition(oldWorkflowStatus, newWorkflowStatus)) {
          await updateAllOffersForLocation(db, id, userId, preSaveVariations);
        }
      }
      if (Object.prototype.hasOwnProperty.call(cleanedData, 'providedByOrg')) {
        const provided = (cleanedData as any).providedByOrg as any;
        const newOrgId = provided == null ? null : (typeof provided === 'string' ? provided : ((provided as any).id || null));
        if (newOrgId !== oldOrgId) {
          await updateOrgCountForOrgChange(db, id, oldOrgId, newOrgId);
        }
      }

      // Dispatch an event to notify components that a version has been updated
      if (typeof window !== 'undefined') {
        console.log("[campLocationUpdateService] Dispatching locationVersionChanged event");
        const event = new CustomEvent("locationVersionChanged", { 
          detail: { locationId: id, versionId } 
        });
        window.dispatchEvent(event);
      }

      return true;
    }
  } catch (error) {
    console.error("[campLocationUpdateService] Error updating camp location:", error);
    throw error;
  }
};
